import { formatDate } from '@angular/common';

/** Financial report presenters shared by the skinned company profiles (Logistics and Tax). */

export type FinancialReportPeriods = 'latest-two' | 'latest-five';
export type FinancialDirection = 'up' | 'down' | 'flat' | null;

export const FINANCIAL_REPORT_TABS = ['Profit and Loss Account', 'Balance Sheet', 'Asset Breakdown', 'Other Financial Metrics'];
export const ASSET_BREAKDOWN_TAB = 'Asset Breakdown';
/** The most periods the multi-period report shows; the API returns the latest five accounts periods by default. */
export const FINANCIAL_REPORT_MAX_PERIODS = 5;

export interface FinancialFigure {
  name: string;
  current: number | string | null;
  previous: number | string | null;
  change: number | null;
  direction: FinancialDirection;
  type: string;
}

/** A section as the API returns it in financials.accounts: one column per filed period, newest first. */
export interface RawFinancialSection {
  columns?: Array<{ column_name: string; submitted?: string; url_to_pdf_file?: string }>;
  rows?: Array<{ name: string; data?: Record<string, unknown>; type?: string }>;
}

export interface FinancialReportColumn {
  key: string;
  /** Period end, e.g. "31 Dec 2025". */
  label: string;
  accountType: string;
  submitted: string;
  url: string;
}

export interface FinancialReportCell {
  value: unknown;
  display: string;
  negative: boolean;
  /** Change against the next (older) period. */
  change: number | null;
  direction: FinancialDirection;
}

export interface FinancialReportRow {
  name: string;
  type: string;
  cells: FinancialReportCell[];
}

export interface FinancialReportTable {
  name: string;
  columns: FinancialReportColumn[];
  rows: FinancialReportRow[];
}

function numeric(value: unknown): number | null {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : null;
}

/** The largest year-on-year change shown, in either direction. A bigger or infinite change is likely an extraction error. */
export const MAX_SHOWN_CHANGE_PERCENT = 1000;

/**
 * The size of the change from previous to current as a percentage of the earlier figure's size, or null when it should
 * not be shown at all: not finite (for example against a nil earlier figure) or over 1000% either way.
 */
function shownChangeMagnitude(current: number, previous: number): number | null {
  const magnitude = Math.abs((current - previous) / previous * 100);
  return Number.isFinite(magnitude) && magnitude <= MAX_SHOWN_CHANGE_PERCENT ? magnitude : null;
}

/**
 * Year-on-year movement for the multi-period table. Equal figures are flat. A change that is not finite or is over
 * 1000% either way is not shown at all (no direction). Otherwise the direction is given, and the percentage is omitted
 * (not meaningful) when the earlier figure is negative or the figure turns negative.
 */
export function financialChange(current: unknown, previous: unknown): { change: number | null; direction: FinancialDirection } {
  const a = numeric(current);
  const b = numeric(previous);
  if (current === null || previous === null || a === null || b === null) return { change: null, direction: null };
  if (a === b) return { change: b > 0 ? 0 : null, direction: 'flat' };
  const magnitude = shownChangeMagnitude(a, b);
  if (magnitude === null) return { change: null, direction: null };
  return { change: b > 0 && a >= 0 ? magnitude : null, direction: a > b ? 'up' : 'down' };
}

export function financialSections(accounts: Record<string, any> | null | undefined): Array<{ name: string; columns: string[]; rows: FinancialFigure[] }> {
  return Object.entries(accounts || {}).map(([name, section]) => {
    const columns = (section?.columns || []).map((column: any) => column.column_name);
    const rows = (section?.rows || []).map((row: any) => {
      const current = row.data?.[columns[0]] ?? null;
      const previous = row.data?.[columns[1]] ?? null;
      const a = typeof current === 'number' ? current : Number(current);
      const b = typeof previous === 'number' ? previous : Number(previous);
      const isNumeric = current !== null && previous !== null && Number.isFinite(a) && Number.isFinite(b);
      // Same rule as financialChange: a change that is not finite or is over 1000% either way is not shown at all.
      const change = !isNumeric ? null : a === b ? 0 : shownChangeMagnitude(a, b);
      const direction: FinancialDirection = change === null ? null : a > b ? 'up' : a < b ? 'down' : 'flat';
      return { name: row.name, current, previous, change, direction, type: row.type || 'number' };
    });
    return { name, columns, rows };
  });
}

export function financialRow(sections: ReturnType<typeof financialSections>, name: string): FinancialFigure | null {
  return sections.flatMap(section => section.rows).find(row => row.name.toLowerCase() === name.toLowerCase() && row.current !== null) || null;
}

/**
 * Turns asset_breakdown ({ '2025-12-31': { tangible: { vehicles: 1 } } }) into an accounts-style section with one
 * column per period end, newest first, and one "category · label" row per recorded asset class.
 */
export function assetBreakdownSection(assetBreakdown: Record<string, any> | null | undefined): RawFinancialSection {
  const rows = new Map<string, Record<string, number>>();
  const dates = new Set<string>();
  for (const [date, categories] of Object.entries(assetBreakdown || {})) {
    for (const [category, figures] of Object.entries((categories || {}) as Record<string, any>)) {
      for (const [label, value] of Object.entries((figures || {}) as Record<string, any>)) {
        if (typeof value !== 'number') continue;
        dates.add(date);
        const name = `${category} · ${label.replace(/_/g, ' ')}`;
        rows.set(name, { ...(rows.get(name) || {}), [date]: value });
      }
    }
  }
  return {
    columns: [...dates].sort().reverse().map(column_name => ({ column_name })),
    rows: [...rows].map(([name, data]) => ({ name, data, type: 'funds' })),
  };
}

export function assetBreakdownRows(assetBreakdown: Record<string, any> | null | undefined): FinancialFigure[] {
  return financialSections({ [ASSET_BREAKDOWN_TAB]: assetBreakdownSection(assetBreakdown) })[0].rows;
}

/** The tab to open: the requested one when it has figures, otherwise the first that does. */
export function financialReportInitialTab(accounts: Record<string, any> | null | undefined, assetBreakdown: Record<string, any> | null | undefined, requested = FINANCIAL_REPORT_TABS[0]): string {
  const sections = financialSections(accounts);
  const assetRows = assetBreakdownRows(assetBreakdown);
  if (requested === ASSET_BREAKDOWN_TAB ? assetRows.length : sections.some(section => section.name === requested && section.rows.length)) return requested;
  return sections.find(section => section.rows.length)?.name || (assetRows.length ? ASSET_BREAKDOWN_TAB : FINANCIAL_REPORT_TABS[0]);
}

export function hasFinancialReport(accounts: Record<string, any> | null | undefined, assetBreakdown: Record<string, any> | null | undefined): boolean {
  return financialSections(accounts).some(section => section.rows.length > 0) || assetBreakdownRows(assetBreakdown).length > 0;
}

/** The number of distinct filed periods across the accounts and the asset breakdown, capped at the five the report shows. */
export function financialReportPeriodCount(accounts: Record<string, any> | null | undefined, assetBreakdown: Record<string, any> | null | undefined): number {
  const periods = new Set<string>();
  for (const section of Object.values(accounts || {}) as RawFinancialSection[]) {
    if (section?.rows?.length) for (const column of section.columns || []) periods.add(periodLabel(column.column_name));
  }
  for (const column of assetBreakdownSection(assetBreakdown).columns || []) periods.add(periodLabel(column.column_name));
  return Math.min(periods.size, FINANCIAL_REPORT_MAX_PERIODS);
}

/** "Latest five filed periods" at the cap, otherwise "3 filed periods" or "1 filed period". */
export function financialPeriodsText(count: number): string {
  if (count >= FINANCIAL_REPORT_MAX_PERIODS) return 'Latest five filed periods';
  return `${count} filed ${count === 1 ? 'period' : 'periods'}`;
}

/** "Y.E. 31 Dec 2025" and "2025-12-31" both become "31 Dec 2025". */
export function periodLabel(columnName: string): string {
  const name = String(columnName || '').replace(/^Y\.E\.\s*/i, '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(name)) return formatDate(name, 'd MMM y', 'en-US');
  return name;
}

function safeUrl(value: unknown): string {
  const text = String(value || '').trim();
  return /^https?:\/\//i.test(text) ? text : '';
}

/**
 * Accountant-friendly multi-year figures: GBP to the pound in en-GB, negatives in brackets, blanks as an en dash.
 */
export function formatReportFigure(value: unknown, type = 'funds'): { display: string; negative: boolean } {
  if (value === null || value === undefined || value === '') return { display: '–', negative: false };
  const number = numeric(value);
  if (number === null || type === 'string' || type === 'date') return { display: String(value), negative: false };
  const formatted = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(Math.abs(number));
  const display = type === 'funds' ? `£${formatted}` : formatted;
  if (number < 0) return { display: type === 'funds' ? `(${display})` : `-${display}`, negative: true };
  return { display, negative: false };
}

/** Filing details for a period column; the asset breakdown borrows them from the accounts period with the same end. */
export interface FinancialPeriodDetails {
  accountType: string;
  submitted: string;
  url: string;
}

/** Columns newest first by period end; left in the API's order (newest first) when any period end does not parse. */
function newestFirst<T extends { column_name: string }>(columns: T[]): T[] {
  const times = columns.map(column => Date.parse(periodLabel(column.column_name)));
  if (times.some(time => Number.isNaN(time))) return columns;
  return columns.map((column, index) => ({ column, time: times[index] })).sort((a, b) => b.time - a.time).map(entry => entry.column);
}

/**
 * Builds the multi-period table for one section. 'latest-two' keeps the latest and prior periods; 'latest-five' keeps
 * at most the five most recent periods, newest first, even if the API sends more. periodDetails maps a period label to
 * its filing details, for columns that do not carry their own.
 */
export function financialReportTable(name: string, section: RawFinancialSection | null | undefined, periods: FinancialReportPeriods, periodDetails: Record<string, FinancialPeriodDetails> = {}): FinancialReportTable {
  const allColumns = section?.columns || [];
  const kept = periods === 'latest-five' ? newestFirst(allColumns).slice(0, FINANCIAL_REPORT_MAX_PERIODS) : allColumns.slice(0, 2);
  const columns = kept.map(column => {
    const label = periodLabel(column.column_name);
    const details = periodDetails[label];
    return {
      key: column.column_name,
      label,
      accountType: details?.accountType || '',
      submitted: column.submitted || details?.submitted || '',
      url: safeUrl(column.url_to_pdf_file) || details?.url || '',
    };
  });
  const rows = (section?.rows || []).map(row => {
    const type = row.type || 'number';
    const values = columns.map(column => row.data?.[column.key] ?? null);
    const cells = values.map((value, index) => {
      const comparable = type === 'funds' || type === 'number';
      const { change, direction } = comparable && index < values.length - 1 ? financialChange(value, values[index + 1]) : { change: null, direction: null };
      return { value, ...formatReportFigure(value, type), change, direction };
    });
    return { name: row.name, type, cells };
  });
  // When older periods were dropped, a row recorded only in those periods would be all blanks, so leave it out.
  const blank = (cell: FinancialReportCell) => cell.value === null || cell.value === undefined || cell.value === '';
  return { name, columns, rows: kept.length < allColumns.length ? rows.filter(row => !row.cells.every(blank)) : rows };
}

/**
 * Period label to filing details: the accounts type from the Account Type row the API puts in Other Financial Metrics,
 * and the submission date and filing link from the accounts columns.
 */
export function financialPeriodDetails(accounts: Record<string, any> | null | undefined): Record<string, FinancialPeriodDetails> {
  const details: Record<string, FinancialPeriodDetails> = {};
  const detail = (column: string) => details[periodLabel(column)] ||= { accountType: '', submitted: '', url: '' };
  for (const section of Object.values(accounts || {}) as RawFinancialSection[]) {
    for (const column of section?.columns || []) {
      const entry = detail(column.column_name);
      entry.submitted ||= column.submitted || '';
      entry.url ||= safeUrl(column.url_to_pdf_file);
    }
    const row = (section?.rows || []).find(candidate => String(candidate?.name || '').toLowerCase() === 'account type');
    for (const [column, value] of Object.entries(row?.data || {})) {
      if (value) detail(column).accountType = String(value);
    }
  }
  return details;
}

/** Period table columns for a financial report table: "Y.E." period ends, the accounts type, filing date and filing link. */
export function financialPeriodTableColumns(table: FinancialReportTable): Array<{
  key: string; label: string; prefix: string; details: string[]; url: string; urlLabel: string; urlAriaLabel: string;
}> {
  return table.columns.map(column => ({
    key: column.key,
    label: column.label,
    prefix: 'Y.E.',
    details: [
      column.accountType,
      column.submitted ? `Filed ${formatDate(column.submitted, 'd MMM y', 'en-US')}` : '',
    ].filter(Boolean),
    url: column.url,
    urlLabel: 'Filing',
    urlAriaLabel: `Open the filed accounts for the period ended ${column.label} (opens in a new tab)`,
  }));
}
